import React, { useState } from 'react';
import { Activity } from '../types';
import { StatusBadge } from '../components/StatusBadge';
import { SummaryCard } from '../components/SummaryCard';
import { SearchIcon, TrendingUpIcon, PlusIcon, CheckCircleIcon, ClockIcon, XCircleIcon, AlertCircleIcon } from 'lucide-react';
interface ProgressPageProps {
  activities: Activity[];
  onViewActivity: (id: string) => void;
}
export function ProgressPage({
  activities,
  onViewActivity
}: ProgressPageProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [unitFilter, setUnitFilter] = useState('Semua Unit');
  const units = ['Semua Unit', ...Array.from(new Set(activities.map(a => a.unit)))];
  const activeActivities = activities.filter(a => a.status === 'Baru' || a.status === 'Berjalan' || a.status === 'Tertunda');
  const filteredActivities = activeActivities.filter(act => {
    const matchSearch = act.title.toLowerCase().includes(searchTerm.toLowerCase()) || act.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchUnit = unitFilter === 'Semua Unit' || act.unit === unitFilter;
    return matchSearch && matchUnit;
  });
  // Extract all progress history for summary and timeline
  const allProgress = activities.flatMap(act => (act.progressHistory || []).map(prog => ({
    ...prog,
    activityTitle: act.title,
    activityCode: act.code
  }))).sort((a, b) => new Date(b.reportedAt).getTime() - new Date(a.reportedAt).getTime());
  const totalProgress = allProgress.length;
  const verifiedProgress = allProgress.filter(p => p.verificationStatus === 'Disetujui').length;
  const pendingProgress = allProgress.filter(p => p.verificationStatus === 'Belum Diverifikasi').length;
  const rejectedProgress = allProgress.filter(p => p.verificationStatus === 'Ditolak' || p.verificationStatus === 'Revisi').length;
  const recentProgress = allProgress.slice(0, 8);
  return <div className="space-y-6 flex flex-col h-full" data-id="element-831">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-832">
        <h2 className="text-2xl font-bold text-[#E2E8F0] uppercase tracking-wide flex items-center" data-id="element-833">
          <TrendingUpIcon className="w-6 h-6 mr-3 text-[#00D4FF]" data-id="element-834" />
          UPDATE PROGRES KEGIATAN
        </h2>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-835">
        <SummaryCard title="Total Laporan Progres" value={totalProgress} icon={<TrendingUpIcon className="w-6 h-6" data-id="element-837" />} iconBgColor="bg-[#00D4FF]/10" iconColor="text-[#00D4FF]" borderColor="border-t-[#00D4FF]" data-id="element-836" />
        <SummaryCard title="Terverifikasi" value={verifiedProgress} icon={<CheckCircleIcon className="w-6 h-6" data-id="element-839" />} iconBgColor="bg-[#00FF88]/10" iconColor="text-[#00FF88]" borderColor="border-t-[#00FF88]" data-id="element-838" />
        <SummaryCard title="Menunggu Verifikasi" value={pendingProgress} icon={<ClockIcon className="w-6 h-6" data-id="element-841" />} iconBgColor="bg-[#FFB800]/10" iconColor="text-[#FFB800]" borderColor="border-t-[#FFB800]" data-id="element-840" />
        <SummaryCard title="Ditolak / Revisi" value={rejectedProgress} icon={<XCircleIcon className="w-6 h-6" data-id="element-843" />} iconBgColor="bg-[#FF3B3B]/10" iconColor="text-[#FF3B3B]" borderColor="border-t-[#FF3B3B]" data-id="element-842" />
      </div>

      <div className="bg-[#111827] p-4 rounded shadow-sm border border-[#1E3A5F] flex flex-wrap gap-4 items-center" data-id="element-844">
        <div className="relative flex-1 min-w-[300px]" data-id="element-845">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-846">
            <SearchIcon className="h-4 w-4 text-[#94A3B8]" data-id="element-847" />
          </div>
          <input type="text" placeholder="Cari kegiatan aktif..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-[#1E3A5F] rounded text-sm focus:ring-1 focus:ring-[#00D4FF] focus:border-[#00D4FF] bg-[#0B0F1A] text-[#E2E8F0] placeholder-[#475569]" data-id="element-848" />
        </div>
        <select value={unitFilter} onChange={e => setUnitFilter(e.target.value)} className="border border-[#1E3A5F] rounded text-sm py-2 pl-3 pr-8 focus:ring-1 focus:ring-[#00D4FF] focus:border-[#00D4FF] bg-[#0B0F1A] text-[#E2E8F0]" data-id="element-849">
          {units.map(u => <option key={u} value={u} data-id="element-850">
              {u}
            </option>)}
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-851">
        {/* Active Activities Table */}
        <div className="lg:col-span-2 bg-[#111827] rounded shadow-lg border border-[#1E3A5F] overflow-hidden flex flex-col" data-id="element-852">
          <div className="p-4 border-b border-[#1E3A5F] bg-[#0F1629]" data-id="element-853">
            <h3 className="text-sm font-semibold text-[#E2E8F0] uppercase tracking-wide flex items-center" data-id="element-854">
              <span className="w-1 h-4 bg-[#00D4FF] mr-2" data-id="element-855"></span>
              KEGIATAN AKTIF
            </h3>
          </div>
          <div className="overflow-x-auto flex-1" data-id="element-856">
            <table className="w-full text-sm text-left" data-id="element-857">
              <thead className="text-xs text-[#94A3B8] uppercase bg-[#0B0F1A] border-b border-[#1E3A5F]" data-id="element-858">
                <tr data-id="element-859">
                  <th className="px-4 py-3 font-semibold w-12 text-center" data-id="element-860">
                    NO
                  </th>
                  <th className="px-4 py-3 font-semibold" data-id="element-861">KODE & JUDUL</th>
                  <th className="px-4 py-3 font-semibold" data-id="element-862">STATUS</th>
                  <th className="px-4 py-3 font-semibold min-w-[150px]" data-id="element-863">
                    PROGRES SAAT INI
                  </th>
                  <th className="px-4 py-3 font-semibold text-center" data-id="element-864">AKSI</th>
                </tr>
              </thead>
              <tbody data-id="element-865">
                {filteredActivities.length > 0 ? filteredActivities.map((act, index) => <tr key={act.id} className="border-b border-[#1E3A5F]/50 hover:bg-[#1A2332] transition-colors" data-id="element-866">
                      <td className="px-4 py-4 text-center text-[#94A3B8] text-sm" data-id="element-867">
                        {index + 1}
                      </td>
                      <td className="px-4 py-4" data-id="element-868">
                        <div className="font-mono text-[#00D4FF] text-xs mb-1" data-id="element-869">
                          {act.code}
                        </div>
                        <div className="font-medium text-[#E2E8F0] text-sm line-clamp-2" data-id="element-870">
                          {act.title}
                        </div>
                      </td>
                      <td className="px-4 py-4" data-id="element-871">
                        <StatusBadge status={act.status} data-id="element-872" />
                      </td>
                      <td className="px-4 py-4" data-id="element-873">
                        <div className="flex items-center justify-between mb-1" data-id="element-874">
                          <span className="text-xs font-mono text-[#00D4FF]" data-id="element-875">
                            {act.progressPercent}%
                          </span>
                        </div>
                        <div className="w-full bg-[#0B0F1A] rounded-full h-2 border border-[#1E3A5F]" data-id="element-876">
                          <div className="h-full rounded-full bg-[#00D4FF] glow-cyan" style={{
                      width: `${act.progressPercent}%`
                    }} data-id="element-877"></div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-center" data-id="element-878">
                        <button onClick={() => onViewActivity(act.id)} className="inline-flex items-center px-3 py-1.5 bg-[#00D4FF]/10 text-[#00D4FF] border border-[#00D4FF]/30 rounded hover:bg-[#00D4FF] hover:text-[#0B0F1A] transition-colors text-xs font-bold uppercase glow-cyan whitespace-nowrap" data-id="element-879">
                          <PlusIcon className="w-3.5 h-3.5 mr-1.5" data-id="element-880" />
                          UPDATE
                        </button>
                      </td>
                    </tr>) : <tr data-id="element-881">
                    <td colSpan={5} className="px-4 py-8 text-center text-[#94A3B8] text-sm" data-id="element-882">
                      Tidak ada kegiatan aktif yang sesuai dengan filter.
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Progress Timeline */}
        <div className="bg-[#111827] rounded shadow-lg border border-[#1E3A5F] overflow-hidden flex flex-col" data-id="element-883">
          <div className="p-4 border-b border-[#1E3A5F] bg-[#0F1629]" data-id="element-884">
            <h3 className="text-sm font-semibold text-[#E2E8F0] uppercase tracking-wide flex items-center" data-id="element-885">
              <span className="w-1 h-4 bg-[#00D4FF] mr-2" data-id="element-886"></span>
              RIWAYAT UPDATE TERBARU
            </h3>
          </div>
          <div className="p-4 overflow-y-auto flex-1" data-id="element-887">
            {recentProgress.length > 0 ? <div className="relative border-l-2 border-[#1E3A5F] ml-3 space-y-6" data-id="element-888">
                {recentProgress.map(prog => <div key={prog.id} className="relative pl-5" data-id="element-889">
                    <div className="absolute -left-[11px] top-1 w-5 h-5 rounded-full bg-[#0B0F1A] border-2 border-[#00D4FF] flex items-center justify-center glow-cyan" data-id="element-890">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#00D4FF]" data-id="element-891"></div>
                    </div>

                    <div className="bg-[#0B0F1A] border border-[#1E3A5F] rounded p-3 hover:border-[#00D4FF]/50 transition-colors" data-id="element-892">
                      <div className="flex justify-between items-start mb-2" data-id="element-893">
                        <button onClick={() => onViewActivity(prog.activityId)} className="font-mono text-xs text-[#00D4FF] hover:underline text-left" data-id="element-894">
                          {prog.activityCode}
                        </button>
                        <span className="font-mono text-xs font-bold text-[#00D4FF] bg-[#00D4FF]/10 px-1.5 py-0.5 rounded" data-id="element-895">
                          {prog.progressPercent}%
                        </span>
                      </div>

                      <p className="text-sm text-[#E2E8F0] mb-2 line-clamp-2" data-id="element-896">
                        {prog.progressNote}
                      </p>

                      {prog.obstacleNote && prog.obstacleNote !== 'Nihil.' && <p className="text-xs text-[#FF3B3B] mb-2 flex items-center line-clamp-1" data-id="element-897">
                          <AlertCircleIcon className="w-3 h-3 mr-1 flex-shrink-0" data-id="element-898" />
                          {prog.obstacleNote}
                        </p>}

                      <div className="flex justify-between items-center mt-3 pt-2 border-t border-[#1E3A5F]/50" data-id="element-899">
                        <div className="text-xs text-[#94A3B8]" data-id="element-900">
                          {prog.reportedBy}
                        </div>
                        <div className="text-xs font-mono text-[#64748B]" data-id="element-901">
                          {new Date(prog.reportedAt).toLocaleDateString('id-ID')}
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div> : <div className="text-center py-8 text-[#94A3B8]" data-id="element-902">
                <p className="text-sm" data-id="element-903">Belum ada riwayat progres.</p>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}